import { useEffect, useRef } from "react";

const ESCAPE_FOCUS_SETTLE_MS = 200;

type ReportFrameProps = {
  src: string;
  title: string;
  height?: string;
};

export default function ReportFrame({
  src,
  title,
  height = "100%",
}: ReportFrameProps) {
  const afterReportRef = useRef<HTMLDivElement>(null);
  const skipLinkRef = useRef<HTMLAnchorElement>(null);
  const outerFrameRef = useRef<HTMLIFrameElement>(null);

  useEffect(() => {
    const outerFrame = outerFrameRef.current;
    if (!outerFrame) return;

    let detachKeydown: (() => void) | undefined;
    let detachInnerLoad: (() => void) | undefined;
    let observer: MutationObserver | undefined;
    let pendingEscapeCheck: number | undefined;

    const onEscapeKeyDown = (e: KeyboardEvent) => {
      if (e.key !== "Escape" || e.repeat) return;
      const doc = e.currentTarget as Document;
      const focusedBefore = doc.activeElement;
      window.clearTimeout(pendingEscapeCheck);
      pendingEscapeCheck = window.setTimeout(() => {
        if (doc.activeElement === focusedBefore) {
          skipLinkRef.current?.focus();
        }
      }, ESCAPE_FOCUS_SETTLE_MS);
    };

    const bindEscapeExit = (doc: Document) => {
      doc.addEventListener("keydown", onEscapeKeyDown, true);
      return () => doc.removeEventListener("keydown", onEscapeKeyDown, true);
    };

    // Newly inserted iframes start on a placeholder about:blank document
    // before navigating to their real content, and some scripts insert the
    // element first and set its destination afterward, which fires a second
    // load event. Rebinding on every load (and detaching the previous
    // listener first) makes sure the listener always ends up on whichever
    // document is current once the real content finishes loading, whether
    // that takes one load event or two.
    const bindOnceInnerFrameLoaded = (innerFrame: HTMLIFrameElement) => {
      const onInnerLoad = () => {
        detachKeydown?.();
        detachKeydown = undefined;
        try {
          const innerDoc = innerFrame.contentWindow?.document;
          if (innerDoc) detachKeydown = bindEscapeExit(innerDoc);
        } catch {
          // Report canvas ended up on a different origin than expected;
          // nothing we can reach from here.
        }
      };
      innerFrame.addEventListener("load", onInnerLoad);
      detachInnerLoad = () =>
        innerFrame.removeEventListener("load", onInnerLoad);
      onInnerLoad(); // covers the case where it's already finished loading
    };

    const watchForInnerFrame = (outerDoc: Document) => {
      let currentInnerFrame: HTMLIFrameElement | null = null;

      const syncInnerFrame = () => {
        const innerFrame = outerDoc.querySelector("iframe");
        if (innerFrame === currentInnerFrame) return;
        currentInnerFrame = innerFrame;

        detachInnerLoad?.();
        detachInnerLoad = undefined;
        detachKeydown?.();
        detachKeydown = undefined;

        if (innerFrame) bindOnceInnerFrameLoaded(innerFrame);
      };

      syncInnerFrame();

      observer = new MutationObserver(syncInnerFrame);
      observer.observe(outerDoc.documentElement ?? outerDoc, {
        childList: true,
        subtree: true,
      });
    };

    const onOuterLoad = () => {
      try {
        const outerDoc = outerFrame.contentWindow?.document;
        if (outerDoc) watchForInnerFrame(outerDoc);
      } catch {
        // Report ended up on a different origin than expected; nothing we
        // can reach from here, so Escape falls back to Power BI's own handling.
      }
    };

    outerFrame.addEventListener("load", onOuterLoad);
    onOuterLoad();

    return () => {
      outerFrame.removeEventListener("load", onOuterLoad);
      observer?.disconnect();
      window.clearTimeout(pendingEscapeCheck);
      detachInnerLoad?.();
      detachKeydown?.();
    };
  }, [src]);

  return (
    <div className="report-frame">
      <a
        ref={skipLinkRef}
        href="#"
        className="visually-hidden-focusable"
        onClick={(e) => {
          e.preventDefault();
          afterReportRef.current?.focus();
        }}
      >
        Skip Power BI report
      </a>
      <p className="report-frame__tip visually-hidden text-secondary small mb-2 mt-2">
        Keyboard tip: inside the report below, press <strong>Escape</strong> to
        exit a visual, and keep pressing <strong>Escape</strong> until you reach
        the skip link above the report.
      </p>
      <iframe
        ref={outerFrameRef}
        src={src}
        title={title}
        width="100%"
        height={height}
        allowFullScreen
        style={{
          border: "none",
          display: "block",
          flex: "1 1 auto",
          minHeight: 0,
        }}
      />
      <div
        ref={afterReportRef}
        tabIndex={-1}
        className="visually-hidden-focusable"
      >
        End of Power BI report
      </div>
    </div>
  );
}
